import React, { useState, useEffect } from 'react';
import {
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Users,
  Filter,
  Database
} from 'lucide-react';
import { toast } from 'react-toastify';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import facebookPagesService from '../../../services/integrations/facebookPagesService';

interface FacebookLeadForm {
  id: number;
  uuid: string;
  form_id: string;
  form_name: string;
  form_status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  page_id: string;
  created_time?: string;
  field_mapping?: any;
  last_sync_at?: string;
  total_leads_synced: number;
  is_active: boolean;
  formFields?: any[];
}

interface FacebookLeadSyncProps {
  form: FacebookLeadForm;
  isOpen: boolean;
  onClose: () => void;
  onSync: (formId: string) => Promise<void>;
}

const FacebookLeadSync: React.FC<FacebookLeadSyncProps> = ({
  form,
  isOpen,
  onClose,
  onSync
}) => {
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [leadPreview, setLeadPreview] = useState<{
    count: number;
    newLeads: number;
    duplicates: number;
    leads: any[];
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLeadPreview(null);
    }
  }, [isOpen]);

  const handlePreviewLeads = async () => {
    setIsLoadingPreview(true);
    try {
      const response = await facebookPagesService.previewLeads(form.form_id);

      if (response.success && response.data) {
        setLeadPreview(response.data);
      } else {
        throw new Error(response.message || 'Failed to preview leads');
      }
    } catch (error: any) {
      console.error('Error previewing leads:', error);
      toast.error(error.message || 'Failed to preview leads');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSync = async () => {
    if (!leadPreview) {
      toast.error('Please preview leads first');
      return;
    }

    setIsSyncing(true);
    try {
      const response = await facebookPagesService.syncLeads(form.form_id);

      if (response.success && response.data) {
        const { synced, duplicates, errors } = response.data;

        if (synced > 0) {
          toast.success(`Successfully synced ${synced} leads! ${duplicates > 0 ? `${duplicates} duplicates skipped.` : ''}`);
        }

        if (errors > 0) {
          toast.warning(`${errors} leads had errors during sync.`);
        }

        if (synced === 0 && errors === 0) {
          toast.info('No new leads to sync.');
        }

        // Call the parent onSync callback to refresh the form data
        await onSync(form.form_id);
        onClose();
      } else {
        throw new Error(response.message || 'Failed to sync leads');
      }
    } catch (error: any) {
      console.error('Error syncing leads:', error);
      toast.error(error.message || 'Failed to sync leads');
    } finally {
      setIsSyncing(false);
    }
  };


  if (!isOpen) return null;

  return (
    <SettingsSlideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Sync Facebook Leads"
      subtitle={`Form ID: ${form.form_id}`}
      icon={<Database className="w-6 h-6" />}
    >
      <>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Lead Sync Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">Recent Leads Sync</h3>
              <div className="text-sm text-blue-700 space-y-1">
                <p>This will sync the most recent 30 leads from Facebook</p>
                <p>Form: {form.form_name}</p>
                <p>Status: {form.form_status}</p>
                {form.last_sync_at && (
                  <p>Last sync: {new Date(form.last_sync_at).toLocaleString()}</p>
                )}
                {form.field_mapping && Object.keys(form.field_mapping).length > 0 && (
                  <p>Field mappings: {Object.keys(form.field_mapping).length} configured</p>
                )}
              </div>
            </div>

          {/* Preview Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Lead Preview</h3>
              <button
                onClick={handlePreviewLeads}
                disabled={isLoadingPreview}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Filter className={`h-4 w-4 ${isLoadingPreview ? 'animate-spin' : ''}`} />
                <span>{isLoadingPreview ? 'Loading...' : 'Preview Leads'}</span>
              </button>
            </div>

            {isLoadingPreview && (
              <div className="flex items-center justify-center h-20 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <RefreshCw className="h-5 w-5 animate-spin text-blue-500" />
                  <span className="text-gray-600">Checking leads from Facebook...</span>
                </div>
              </div>
            )}

            {leadPreview && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="space-y-1">
                    <div className="flex items-center justify-center space-x-1">
                      <Users className="h-4 w-4 text-blue-500" />
                      <span className="text-2xl font-bold text-blue-600">{leadPreview.count}</span>
                    </div>
                    <p className="text-sm text-gray-600">Total Leads</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-center space-x-1">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-2xl font-bold text-green-600">{leadPreview.newLeads}</span>
                    </div>
                    <p className="text-sm text-gray-600">New Leads</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-center space-x-1">
                      <AlertCircle className="h-4 w-4 text-yellow-500" />
                      <span className="text-2xl font-bold text-yellow-600">{leadPreview.duplicates}</span>
                    </div>
                    <p className="text-sm text-gray-600">Duplicates</p>
                  </div>
                </div>

                {leadPreview.newLeads > 0 && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-green-700">
                        {leadPreview.newLeads} new leads will be added to your CRM
                      </span>
                    </div>
                  </div>
                )}

                {leadPreview.duplicates > 0 && (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-yellow-500" />
                      <span className="text-sm text-yellow-700">
                        {leadPreview.duplicates} duplicates will be skipped (based on Facebook Lead ID)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

            {/* Mapping Warning */}
            {!form.field_mapping || Object.keys(form.field_mapping).length === 0 && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <div>
                    <h4 className="font-medium text-red-900">No Field Mapping Configured</h4>
                    <p className="text-sm text-red-700 mt-1">
                      Please configure field mapping before syncing leads to ensure proper data mapping.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-900">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {leadPreview ? (
                <span className="text-green-600 dark:text-green-400 font-medium">
                  Ready to sync {leadPreview.newLeads} new leads
                </span>
              ) : (
                'Preview leads first to see what will be synced'
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSync}
                disabled={isSyncing || !leadPreview || leadPreview.newLeads === 0 || (!form.field_mapping || Object.keys(form.field_mapping).length === 0)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {isSyncing ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span>{isSyncing ? 'Syncing...' : 'Sync Leads'}</span>
              </button>
            </div>
          </div>
        </div>
      </>
    </SettingsSlideModal>
  );
};

export default FacebookLeadSync;